import { memo } from 'react';
import { NavigateFunction, Route, Routes, useNavigate, useResolvedPath } from 'react-router-dom';
import { getIsLockerOpen, IAppointment } from '../../../../API/GoMoto';
import { useAppointmentUpdateAndPersist } from '../../../../context/Appointment/appointment';
import { useFullReset } from '../../../../context/Kiosk/kiosk';
import { Page } from '../../../Layout/Page/Page';
import { LockerOpenIssue } from './LockerOpenIssue/LockerOpenIssue';
import { DoubleCheckLocker } from './DoubleCheckLocker/DoubleCheckLocker';
import { LoadAppointment } from '../../../Utility/LoadAppointment';
import { PickUpAtLocker } from './PickUpAtLocker/PickUpAtLocker';
import store from 'store2';
import { OutdoorVehiclePickUpRoutePaths, PathName } from '../../../../helpers/Routes';
import { GoMotoComponent, GoMotoFunction } from '../../../../API/GoMoto.types';
import { PickUpComplete } from './PickUpComplete/PickUpComplete';

/**
 * Routing component that manages the routes to every screen needed as a part of the 365 Kiosk pick-up
 * process for the customer to retrieve their key or perform a key exchange.
 */
export const OutdoorPickUpIndex: GoMotoComponent = memo(() => {
    const { pathname } = useResolvedPath('.');
    const navigate: NavigateFunction = useNavigate();
    const reset: VoidFunction = useFullReset(true, false);
    const saveAppointment = useAppointmentUpdateAndPersist();

    const tempAppointment: IAppointment | null = store.session.get('tempAppointment');

    const onReopenLocker: GoMotoFunction<[void], Promise<void>> = async () => {
        const tempAppt: IAppointment | null = store.session.get('tempAppointment');

        if (tempAppt) {
            getIsLockerOpen(tempAppt.kioskKey, tempAppt.keySlotNumber ?? 0)
                .then(async (isOpen) => {
                    if (!isOpen) {
                        // if the locker is open, avoid setting it to a state that implies it still needs to be opened
                        await saveAppointment({ ...tempAppt,
                            keyStatus: 'reopen requested pickup' });
                    }
                })
                .finally(() => {
                    navigate(PathName(pathname, OutdoorVehiclePickUpRoutePaths.Locker));
                });
        }
    };

    return (
        <Page>
            <Routes>
                <Route
                    path={PathName(OutdoorVehiclePickUpRoutePaths.Appointment, ':apptKey', '*')}
                    element={
                        <LoadAppointment
                            onLoaded={(appointment: IAppointment) => {
                                if (
                                    appointment.status === 'completed' ||
                                    (appointment.keyStatus !== 'slot_assigned' && appointment.keyStatus !== 'reopen requested pickup')
                                ) {
                                    reset();
                                }
                                else {
                                    store.session.set('tempAppointment', appointment);

                                    navigate(PathName(pathname, OutdoorVehiclePickUpRoutePaths.Locker));
                                }
                            }}
                        />
                    }
                />
                <Route
                    path={PathName(OutdoorVehiclePickUpRoutePaths.Locker, '*')}
                    element={tempAppointment && (
                        <PickUpAtLocker
                            onLockerClosed={() => {
                                navigate(PathName(pathname, OutdoorVehiclePickUpRoutePaths.DoubleCheck));
                            }}
                            onLockerDidntOpen={() => {
                                navigate(PathName(pathname, OutdoorVehiclePickUpRoutePaths.Help));
                            }}
                            appointment={tempAppointment}
                        />
                    )}
                />
                <Route
                    path={PathName(OutdoorVehiclePickUpRoutePaths.DoubleCheck, '*')}
                    element={tempAppointment && (
                        // TODO: Replace this with the new `GMDoubleCheckLocker` control. Will require use of the appointment context rather than just session storage.
                        <DoubleCheckLocker
                            onContinue={() => {
                                navigate(PathName(pathname, OutdoorVehiclePickUpRoutePaths.Complete));
                            }}
                            onReopenLocker={onReopenLocker}
                            appointment={tempAppointment}
                        />
                    )}
                />
                <Route
                    path={PathName(OutdoorVehiclePickUpRoutePaths.Complete, '*')}
                    element={tempAppointment && (
                        <PickUpComplete
                            onFinish={reset}
                            appointment={tempAppointment}
                        />
                    )}
                />
                <Route
                    path={PathName(OutdoorVehiclePickUpRoutePaths.Help, '*')}
                    element={
                        <LockerOpenIssue
                            onTryAgain={() => {
                                navigate(PathName(pathname, OutdoorVehiclePickUpRoutePaths.Locker));
                            }}
                        />
                    }
                />
            </Routes>
        </Page>
    );
});
